import { FieldArray, FieldArrayRenderProps, FormikValues, useFormikContext } from 'formik'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Api from 'src/api'
import { ArrowDown } from 'src/assets/icons/customIcons/ArrowDown'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { DeleteIcon } from 'src/assets/icons/customIcons/upload-icons/Delete'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Form } from 'src/views/components/forms/formik/Form'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Swal from 'sweetalert2'
import { useSection, useSectionData } from './SectionContext'
import {
  buildBodyWithCompetencies,
  getCompetenciesInitialValues,
  submitSection,
  type CompetencyFormValues,
} from './sectionFormState'
import { TabFormErrors } from './sectionTabForm'

interface Criteria {
  readonly id: string
  readonly min: number
  readonly max: number
}

interface Competency {
  readonly id: string
  readonly name: string
  readonly minScore: number
  readonly maxScore: number
  readonly criterias: Criteria[]
}

interface FormValues {
  readonly competencies: Competency[]
}

interface SelectOption {
  readonly value: string
  readonly label: string
}

const EMPTY_COMPETENCY: Competency = {
  id: '',
  name: '',
  minScore: 0,
  maxScore: 0,
  criterias: [],
}

const EMPTY_CRITERIA: Criteria = {
  id: '',
  min: 0,
  max: 0,
}

export default function AdministratorPortfolioSectionCompetencies(): JSX.Element {
  const section = useSectionData()
  const { mutate } = useSection()
  const headers = useAuthenticatedHeaders()
  const navigate = useNavigate()
  const locale = useLocale()
  const t = useTranslatable()
  const { id } = useParams<{ id: string }>()

  const initialValues = React.useMemo(() => getCompetenciesInitialValues(section), [section])

  const handleSubmit = React.useCallback(
    async (values: { competencies: CompetencyFormValues[] }) => {
      const body = buildBodyWithCompetencies(section, values.competencies)
      await submitSection({ headers, id, body, navigate, locale, mutate, t })
    },
    [section, headers, id, navigate, locale, mutate, t]
  )

  return (
    <Form initialValues={initialValues} enableReinitialize onSubmit={handleSubmit} classNames={{ form: 'w-full' }}>
      <CompetenciesFormContent />
    </Form>
  )
}

function CompetenciesFormContent(): JSX.Element {
  const t = useTranslatable()
  const { values } = useFormikContext<FormValues>()
  const headers = useAuthenticatedHeaders()
  const { data: portfolioListsCompetencies } = useApi({
    endpoint: Api.getAdministratorPortfolioListsCompetencies,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  const { data: portfolioListsCriterias } = useApi({
    endpoint: Api.getAdministratorPortfolioListsCriterias,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  const competenciesOptions = React.useMemo<SelectOption[]>(
    () => portfolioListsCompetencies?.map((c) => ({ value: c.id.toString(), label: c.name })) ?? [],
    [portfolioListsCompetencies]
  )

  const criteriasOptions = React.useMemo<SelectOption[]>(
    () => portfolioListsCriterias?.map((c) => ({ value: c.id.toString(), label: c.name })) ?? [],
    [portfolioListsCriterias]
  )
  const confirmDelete = React.useCallback(
    async (index: number, remove: (index: number) => void) => {
      const result = await Swal.fire({
        title: t('common:do_you_really_want_to_delete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#0D6EFD',
        cancelButtonColor: '#6C757D',
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
      })
      if (result.isConfirmed) remove(index)
    },
    [t]
  )

  if (!values.competencies?.length) {
    return (
      <div className="mt-6 flex w-full flex-col items-center gap-4 py-8">
        <TabFormErrors />
        <FieldArray name="competencies">
          {({ push }: FieldArrayRenderProps) => (
            <Button
              variant="blue"
              type="button"
              className="flex items-center gap-2"
              onClick={() => push(EMPTY_COMPETENCY)}
            >
              <Plus color="white" />
              <span>{t('portfolio:add_competency')}</span>
            </Button>
          )}
        </FieldArray>
        <CompetenciesSaveButton />
      </div>
    )
  }

  return (
    <div className="mt-6 flex w-full flex-col gap-4">
      <TabFormErrors />
      <FieldArray name="competencies">
        {({ remove, push }: FieldArrayRenderProps) => (
          <>
            {values.competencies.map((competency, index) => (
              <CompetencyCard
                key={index}
                index={index}
                competency={competency}
                competenciesOptions={competenciesOptions}
                criteriasOptions={criteriasOptions}
                onDelete={() => confirmDelete(index, remove)}
                onDeleteCriteria={confirmDelete}
              />
            ))}
            <Button
              variant="blue"
              type="button"
              className="flex w-full items-center justify-center gap-2"
              onClick={() => push(EMPTY_COMPETENCY)}
            >
              <Plus color="white" />
              <span>{t('portfolio:add_competency')}</span>
            </Button>
          </>
        )}
      </FieldArray>
      <CompetenciesSaveButton />
    </div>
  )
}

interface CompetencyCardProps {
  readonly index: number
  readonly competency: Competency
  readonly competenciesOptions: readonly SelectOption[]
  readonly criteriasOptions: readonly SelectOption[]
  readonly onDelete: () => void
  readonly onDeleteCriteria: (index: number, remove: (index: number) => void) => void
}

const CompetencyCard = React.memo(function CompetencyCard({
  index,
  competency,
  competenciesOptions,
  criteriasOptions,
  onDelete,
  onDeleteCriteria,
}: CompetencyCardProps): JSX.Element {
  const t = useTranslatable()
  const [isExpanded, setIsExpanded] = React.useState(true)
  const toggleExpand = React.useCallback(() => setIsExpanded((prev) => !prev), [])
  const handleDelete = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onDelete()
    },
    [onDelete]
  )

  const selectedOption = competenciesOptions.find((opt) => opt.value === competency.id)

  return (
    <div className="overflow-hidden rounded-card bg-card shadow-sm">
      {/* Header */}
      <div
        className="flex cursor-pointer items-center justify-between gap-4 bg-gradient-to-r from-card to-card/80 p-4 transition-colors hover:from-card/90"
        onClick={toggleExpand}
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex size-8 items-center justify-center rounded-full bg-primaryBlue/10 transition-transform duration-300 ${
              isExpanded ? 'rotate-0' : '-rotate-90'
            }`}
          >
            <ArrowDown bold />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-primaryTextColor">
              {selectedOption?.label || t('portfolio:competency')}
            </span>
            <span className="text-xs text-captionColor">
              {t('common:min')}: {competency.minScore} • {t('common:max')}: {competency.maxScore}
            </span>
          </div>
        </div>
        <Button variant="red" type="button" onClick={handleDelete} className="shrink-0">
          <DeleteIcon color="white" />
        </Button>
      </div>

      {/* Body */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 border-t border-borderGrey p-4">
            <div className="flex w-full flex-wrap items-start justify-between gap-4">
              <div className="w-full min-w-[130px] lg:w-[32%]">
                <SelectInput
                  name={`competencies[${index}].id`}
                  label={t('portfolio:competency')}
                  options={competenciesOptions}
                  required
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="w-[calc(50%-8px)] min-w-[100px] lg:w-[32%]">
                <TextInput type="number" name={`competencies[${index}].minScore`} label={t('common:min')} />
              </div>
              <div className="w-[calc(50%-8px)] min-w-[100px] lg:w-[32%]">
                <TextInput type="number" name={`competencies[${index}].maxScore`} label={t('common:max')} min={1} />
              </div>
            </div>

            <FieldArray name={`competencies[${index}].criterias`}>
              {({ remove, push }: FieldArrayRenderProps) => (
                <div className="flex flex-col gap-3 rounded-lg border border-borderGrey bg-card/50 p-4">
                  <CriteriaList
                    competencyIndex={index}
                    criterias={competency.criterias}
                    criteriasOptions={criteriasOptions}
                    onDelete={(criteriaIndex) => onDeleteCriteria(criteriaIndex, remove)}
                  />
                  <Button
                    variant="blue"
                    type="button"
                    className="flex w-full items-center justify-center gap-2"
                    onClick={() => push(EMPTY_CRITERIA)}
                  >
                    <Plus color="white" />
                    <span>{t('portfolio:add_criteria')}</span>
                  </Button>
                </div>
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </div>
  )
})

interface CriteriaListProps {
  readonly competencyIndex: number
  readonly criterias: readonly Criteria[]
  readonly criteriasOptions: readonly SelectOption[]
  readonly onDelete: (index: number) => void
}

function CriteriaList({ competencyIndex, criterias, criteriasOptions, onDelete }: CriteriaListProps): JSX.Element {
  const t = useTranslatable()

  // Build Set of selected IDs once for O(1) lookups
  const selectedIdsSet = React.useMemo(() => new Set(criterias.map((c) => c.id)), [criterias])

  // Memoize filtered options per criteria - each shows its own value + unselected ones
  const optionsPerCriteria = React.useMemo(() => {
    return criterias.map((criteria) => {
      const currentId = criteria.id
      return criteriasOptions.filter((opt) => opt.value === currentId || !selectedIdsSet.has(opt.value))
    })
  }, [criterias, criteriasOptions, selectedIdsSet])

  if (criterias.length === 0) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-captionColor">
        {t(['კრიტერიუმები არ არის დამატებული', 'No criteria added yet'])}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {criterias.map((_, criteriaIndex) => (
        <CriteriaRow
          key={criteriaIndex}
          competencyIndex={competencyIndex}
          criteriaIndex={criteriaIndex}
          options={optionsPerCriteria[criteriaIndex] ?? []}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}

interface CriteriaRowProps {
  readonly competencyIndex: number
  readonly criteriaIndex: number
  readonly options: readonly SelectOption[]
  readonly onDelete: (index: number) => void
}

const CriteriaRow = React.memo(function CriteriaRow({
  competencyIndex,
  criteriaIndex,
  options,
  onDelete,
}: CriteriaRowProps): JSX.Element {
  const t = useTranslatable()
  const handleDelete = React.useCallback(() => onDelete(criteriaIndex), [onDelete, criteriaIndex])

  return (
    <div className="flex w-full flex-wrap items-end justify-between gap-4 rounded-lg border border-borderGrey/50 bg-card p-3">
      <div className="w-full min-w-[130px] lg:w-[32%]">
        <SelectInput
          name={`competencies[${competencyIndex}].criterias[${criteriaIndex}].id`}
          label={t('portfolio:competency_criteria')}
          options={options}
          required
          menuPortalTarget={document.body}
        />
      </div>
      <div className="w-[calc(50%-8px)] min-w-[100px] lg:w-[32%]">
        <TextInput
          type="number"
          name={`competencies[${competencyIndex}].criterias[${criteriaIndex}].min`}
          label={t('common:min')}
          min={0}
        />
      </div>
      <div className="flex w-[calc(50%-8px)] min-w-[100px] items-start justify-between gap-2 lg:w-[32%]">
        <TextInput
          type="number"
          name={`competencies[${competencyIndex}].criterias[${criteriaIndex}].max`}
          label={t('common:max')}
          min={1}
        />
        <Button variant="red" type="button" onClick={handleDelete} className="mt-[31px]">
          <DeleteIcon color="white" />
        </Button>
      </div>
    </div>
  )
})

function CompetenciesSaveButton(): JSX.Element | null {
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const formik = useFormikContext<FormikValues>()

  if (!canManagePortfolio) return null
  return (
    <div className="my-4 flex flex-wrap justify-end">
      <Button type="submit" className="ml-2" variant="red" disabled={formik.isSubmitting || !formik.dirty}>
        {t('common:save')}
      </Button>
    </div>
  )
}
